import { useState } from 'react';
import { useUsers, useDeactivatedUsers, useUpdateUserRole, useSetUserActive, type UserWithRole } from '@/hooks/useUsers';
import { UserPermissionsDialog } from '@/components/settings/UserPermissionsDialog';
import { PERMISSIONS } from '@/lib/permissions';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { InviteUserDialog } from '@/components/settings/InviteUserDialog';
import { InvitesTable } from '@/components/settings/InvitesTable';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Shield, Mail, Calendar, UserMinus, UserCheck, Key, KeyRound, Clock, RefreshCw } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { useTimezoneUtils } from '@/hooks/useTimezoneUtils';
import { useTenant } from '@/hooks/useTenant';
import { useCtmUserLinks, useRetryCtmUserSync, type CtmUserLink, type CtmUserLinkStatus } from '@/hooks/useCtmUserLinks';

const roleColors = {
  admin: 'bg-primary/10 text-primary',
  csr: 'bg-info/10 text-info',
  affiliate: 'bg-muted text-muted-foreground',
  tech: 'bg-success/10 text-success',
};

const roleLabels = {
  admin: 'Administrator',
  csr: 'Customer Service Rep',
  affiliate: 'Affiliate',
  tech: 'Technician (app)',
};

// Softphone column: the CTM agent login the reconciler keeps for each CSR/admin
// of an agency-mode tenant (public.ctm_user_links).
const ctmStatusStyles: Record<CtmUserLinkStatus, { label: string; className: string }> = {
  synced: { label: 'Synced', className: 'bg-success/10 text-success border-success/20' },
  pending: { label: 'Pending', className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  running: { label: 'Pending', className: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  error: { label: 'Failed', className: 'bg-destructive/10 text-destructive border-destructive/20' },
  removed: { label: 'Removed', className: 'bg-muted text-muted-foreground' },
  skipped: { label: '—', className: 'bg-muted text-muted-foreground' },
};

function CtmSyncBadge({ link }: { link: CtmUserLink | undefined }) {
  if (!link || link.status === 'skipped') return <span className="text-muted-foreground">—</span>;
  const style = ctmStatusStyles[link.status];
  return (
    <Badge variant="outline" className={style.className} title={link.last_error ?? undefined}>
      {style.label}
    </Badge>
  );
}

const isCtmSyncBusy = (link: CtmUserLink | undefined) => link?.status === 'pending' || link?.status === 'running';

export function Users() {
  const { formatDate } = useTimezoneUtils();
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id: string; name: string; currentRole: string } | null>(null);
  const [newRole, setNewRole] = useState<string>('');
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<{ id: string; name: string } | null>(null);
  const [reactivateDialogOpen, setReactivateDialogOpen] = useState(false);
  const [userToReactivate, setUserToReactivate] = useState<{ id: string; name: string } | null>(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [userToResetPassword, setUserToResetPassword] = useState<{ email: string; name: string } | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [permissionsUser, setPermissionsUser] = useState<UserWithRole | null>(null);

  const { data: users = [], isLoading } = useUsers();
  const { data: deactivatedUsers = [], isLoading: isLoadingDeactivated } = useDeactivatedUsers();
  const updateRoleMutation = useUpdateUserRole();
  const setUserActiveMutation = useSetUserActive();
  const { user: currentUser } = useAuth();
  const { company } = useTenant();
  // Only tenants on a Steddy-provisioned CTM sub-account get CTM agents created for them.
  const showCtm = company?.ctm_mode === 'agency' && !!company?.call_tracking_enabled;
  const { data: ctmLinks } = useCtmUserLinks(showCtm);
  const retryCtmSync = useRetryCtmUserSync();

  const handleRoleChange = () => {
    if (!selectedUser) return;

    updateRoleMutation.mutate(
      { userId: selectedUser.id, newRole: newRole as 'admin' | 'csr' },
      {
        onSuccess: () => {
          setRoleDialogOpen(false);
          setSelectedUser(null);
        },
      }
    );
  };

  const handleDeactivateUser = () => {
    if (!userToDeactivate) return;

    setUserActiveMutation.mutate(
      { userId: userToDeactivate.id, active: false },
      {
        onSuccess: () => {
          setDeactivateDialogOpen(false);
          setUserToDeactivate(null);
        },
      },
    );
  };

  const handleReactivateUser = () => {
    if (!userToReactivate) return;

    setUserActiveMutation.mutate(
      { userId: userToReactivate.id, active: true },
      {
        onSuccess: () => {
          setReactivateDialogOpen(false);
          setUserToReactivate(null);
        },
      },
    );
  };

  const openRoleDialog = (userId: string, name: string, currentRole: string) => {
    setSelectedUser({ id: userId, name, currentRole });
    setNewRole(currentRole);
    setRoleDialogOpen(true);
  };

  const handleResetPassword = async () => {
    if (!userToResetPassword || !newPassword) return;
    
    setIsResettingPassword(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-user-password', {
        body: {
          email: userToResetPassword.email,
          newPassword: newPassword,
        },
      });

      if (error) throw error;

      toast.success(`Password updated successfully for ${userToResetPassword.name}`);
      setPasswordDialogOpen(false);
      setUserToResetPassword(null);
      setNewPassword('');
    } catch (error: any) {
      console.error('Error updating password:', error);
      toast.error(error.message || 'Failed to update password');
    } finally {
      setIsResettingPassword(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground">
            Manage users and send invitations
          </p>
        </div>
        <InviteUserDialog />
      </div>

      <Tabs defaultValue="users" className="w-full">
        <TabsList>
          <TabsTrigger value="users">Active Users</TabsTrigger>
          <TabsTrigger value="deactivated">
            Deactivated{deactivatedUsers.length > 0 ? ` (${deactivatedUsers.length})` : ''}
          </TabsTrigger>
          <TabsTrigger value="invites">Pending Invites</TabsTrigger>
        </TabsList>
        
        <TabsContent value="users" className="space-y-4">

        {isLoading ? (
          <div className="text-center py-12">Loading users...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No users found
          </div>
        ) : (
          <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Role</TableHead>
                {showCtm && <TableHead>Softphone</TableHead>}
                <TableHead>Joined</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => {
                const isCurrentUser = user.id === currentUser?.id;
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      {user.full_name}
                      {isCurrentUser && (
                        <Badge variant="outline" className="ml-2">You</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {user.email}
                      </div>
                    </TableCell>
                    <TableCell>{user.phone || '-'}</TableCell>
                    <TableCell>
                      <Badge className={roleColors[user.role as keyof typeof roleColors]}>
                        {roleLabels[user.role as keyof typeof roleLabels]}
                      </Badge>
                    </TableCell>
                    {showCtm && (
                      <TableCell>
                        <CtmSyncBadge link={ctmLinks?.get(user.id)} />
                      </TableCell>
                    )}
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {formatDate(user.created_at, 'MMM d, yyyy')}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        {user.last_login_at
                          ? formatDate(user.last_login_at, 'MMM d, yyyy')
                          : <span className="text-xs italic">Never</span>}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openRoleDialog(user.id, user.full_name, user.role)}
                          disabled={isCurrentUser}
                          title="Change user role"
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Change Role
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPermissionsUser(user)}
                          disabled={isCurrentUser}
                          title={user.role === 'admin' ? 'Administrators see everything' : 'What this user can see beyond their own work'}
                        >
                          <KeyRound className="h-4 w-4 mr-2" />
                          Permissions
                          {user.role !== 'admin' && user.permissions.length > 0 && (
                            <Badge variant="secondary" className="ml-2 px-1.5 py-0 text-[11px]">
                              {user.permissions.length === PERMISSIONS.length ? 'Finance' : `${user.permissions.length}/${PERMISSIONS.length}`}
                            </Badge>
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUserToResetPassword({ email: user.email, name: user.full_name });
                            setPasswordDialogOpen(true);
                          }}
                          title="Reset user password"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        {showCtm && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => retryCtmSync.mutate({ userId: user.id })}
                            disabled={retryCtmSync.isPending || isCtmSyncBusy(ctmLinks?.get(user.id))}
                            title="Sync this user to CTM now"
                          >
                            <RefreshCw className={`h-4 w-4${retryCtmSync.isPending && retryCtmSync.variables?.userId === user.id ? ' animate-spin' : ''}`} />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setUserToDeactivate({ id: user.id, name: user.full_name });
                            setDeactivateDialogOpen(true);
                          }}
                          disabled={isCurrentUser}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          title="Deactivate user (preserves history)"
                        >
                          <UserMinus className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
        )}
        </TabsContent>

        <TabsContent value="deactivated" className="space-y-4">
          {isLoadingDeactivated ? (
            <div className="text-center py-12">Loading deactivated users...</div>
          ) : deactivatedUsers.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No deactivated users
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    {showCtm && <TableHead>Softphone</TableHead>}
                    <TableHead>Deactivated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {deactivatedUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.full_name}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-muted-foreground" />
                          {user.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleColors[user.role as keyof typeof roleColors]}>
                          {roleLabels[user.role as keyof typeof roleLabels]}
                        </Badge>
                      </TableCell>
                      {showCtm && (
                        <TableCell>
                          <CtmSyncBadge link={ctmLinks?.get(user.id)} />
                        </TableCell>
                      )}
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {user.deactivated_at ? formatDate(user.deactivated_at, 'MMM d, yyyy') : '-'}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {showCtm && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => retryCtmSync.mutate({ userId: user.id })}
                              disabled={retryCtmSync.isPending || isCtmSyncBusy(ctmLinks?.get(user.id))}
                              title="Sync this user to CTM now"
                            >
                              <RefreshCw className={`h-4 w-4${retryCtmSync.isPending && retryCtmSync.variables?.userId === user.id ? ' animate-spin' : ''}`} />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setUserToReactivate({ id: user.id, name: user.full_name });
                              setReactivateDialogOpen(true);
                            }}
                            title="Reactivate user"
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            Reactivate
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="invites">
          <InvitesTable />
        </TabsContent>
      </Tabs>

      <UserPermissionsDialog
        user={permissionsUser}
        open={!!permissionsUser}
        onOpenChange={(open) => { if (!open) setPermissionsUser(null); }}
      />

      <AlertDialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Change User Role</AlertDialogTitle>
            <AlertDialogDescription>
              Update the role for <strong>{selectedUser?.name}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2 py-4">
            <Label htmlFor="newRole">Select New Role</Label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="csr">Customer Service Rep</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRoleChange}
              disabled={updateRoleMutation.isPending}
            >
              {updateRoleMutation.isPending ? 'Updating...' : 'Update Role'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Deactivate <strong>{userToDeactivate?.name}</strong>? They will no longer
              be able to sign in, but all of their leads, jobs, payments, and other
              activity will stay attributed to them. You can reactivate them at any time
              from the Deactivated tab.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeactivateUser}
              disabled={setUserActiveMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {setUserActiveMutation.isPending ? 'Deactivating...' : 'Deactivate User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={reactivateDialogOpen} onOpenChange={setReactivateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reactivate User</AlertDialogTitle>
            <AlertDialogDescription>
              Reactivate <strong>{userToReactivate?.name}</strong>? They will be able
              to sign in again and will reappear in the active users list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReactivateUser}
              disabled={setUserActiveMutation.isPending}
            >
              {setUserActiveMutation.isPending ? 'Reactivating...' : 'Reactivate User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset User Password</AlertDialogTitle>
            <AlertDialogDescription>
              Set a new password for <strong>{userToResetPassword?.name}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-2 py-4">
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="text"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetPassword}
              disabled={isResettingPassword || !newPassword}
            >
              {isResettingPassword ? 'Updating...' : 'Update Password'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
